/**
 * Request Deduplication Service
 * Prevents duplicate API calls from being made simultaneously
 */

interface PendingRequest {
  promise: Promise<any>;
  timestamp: number;
}

class RequestDeduplicator {
  private pendingRequests: Map<string, PendingRequest> = new Map();
  private readonly REQUEST_TIMEOUT = 30000; // 30 seconds

  /**
   * Execute a request with deduplication
   * @param key - Unique key for the request
   * @param requestFn - Function that returns a promise
   * @returns Promise that resolves to the request result
   */
  async execute<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
    // Check if there's already a pending request for this key
    const existingRequest = this.pendingRequests.get(key);
    
    if (existingRequest) {
      // Check if the request is still valid (not too old)
      const isExpired = Date.now() - existingRequest.timestamp > this.REQUEST_TIMEOUT;
      
      if (!isExpired) {
        // Return the existing promise
        return existingRequest.promise;
      } else {
        // Remove expired request
        this.pendingRequests.delete(key);
      }
    }

    // Create new request
    const promise = requestFn();
    this.pendingRequests.set(key, {
      promise,
      timestamp: Date.now()
    });

    // Clean up when request completes. The caller handles the rejection on `promise`; this
    // side chain must not raise a second, unhandled one.
    promise
      .finally(() => {
        this.pendingRequests.delete(key);
      })
      .catch(() => undefined);

    return promise;
  }

  /**
   * Clear all pending requests
   */
  clear(): void {
    this.pendingRequests.clear();
  }

  /**
   * Get the number of pending requests
   */
  getPendingCount(): number {
    return this.pendingRequests.size;
  }
}

const requestDeduplicator = new RequestDeduplicator();
export default requestDeduplicator;
